import React, { useEffect, useState } from 'react';
import iosImg from '../../assets/ios.png';
import playstoreImg from '../../assets/playstore.png';
import { useTranslation } from 'react-i18next';
import approvedImg from '../../assets/correct.png';
import checkedImg from '../../assets/checked.png';

// Add custom styles for animations
const styles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes slideIn {
    from { opacity: 0; transform: translateX(-30px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes bounceIn {
    0% { opacity: 0; transform: scale(0.3); }
    50% { opacity: 1; transform: scale(1.05); }
    70% { transform: scale(0.9); }
    100% { opacity: 1; transform: scale(1); }
  }
  .animate-fadeIn { animation: fadeIn 0.6s ease-out; }
  .animate-slideIn { animation: slideIn 0.7s ease-out; }
  .animate-bounceIn { animation: bounceIn 0.8s ease-out; }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('already-registered-styles');
  if (!existingStyle) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'already-registered-styles';
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
  }
}

const AlreadyRegistered = ({email,mobile}) => {
    const { t, i18n: {changeLanguage, language} } = useTranslation();
    const [currentLanguage, setCurrentLanguage] = useState(language);
    
    return (
        <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 animate-fadeIn p-4">
            <div className="max-w-2xl w-full bg-white/95 backdrop-blur-xl shadow-2xl rounded-[2rem] overflow-hidden transform animate-bounceIn border border-white/20">
                {/* Header with gradient */}
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-white/10 transform -skew-y-2"></div>
                    <div className="relative z-10">
                        <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg transform animate-pulse">
                            <svg className="w-12 h-12 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <h2 className="text-3xl font-bold text-white mb-2">
                            {t('product_already_registered')}
                        </h2>
                        <p className="text-amber-100 text-lg">Product is already in our system</p>
                    </div>
                </div>

                <div className="p-8 text-center">
                    {/* Enhanced Icon */}
                    <div className="relative mb-8">
                        <div className="absolute inset-0 bg-amber-200 rounded-full animate-ping opacity-20"></div>
                        <img 
                            src={checkedImg} 
                            alt="Already Registered" 
                            className="relative mx-auto h-32 w-32 transform hover:scale-110 transition-transform duration-300"
                        />
                    </div>

                    {/* Registration Info */}
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 mb-8 animate-slideIn">
                        <h3 className="text-xl font-bold text-gray-800 mb-4">Registration Details</h3>
                        <div className="space-y-3">
                            <div className="flex items-center justify-center space-x-2">
                                <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                                </svg>
                                <span className="font-semibold text-gray-700">{t('email')}:</span>
                                <span className="text-gray-600">{email}</span>
                            </div>
                            <div className="flex items-center justify-center space-x-2">
                                <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                                </svg>
                                <span className="font-semibold text-gray-700">{t('mobile')}:</span>
                                <span className="text-gray-600">{mobile}</span>
                            </div>
                        </div>
                    </div>

                    {/* Download Message */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-8">
                        <h3 className="text-xl font-bold text-gray-800 mb-3">Track Your Product</h3>
                        <p className="text-gray-600 text-lg mb-4">
                            {t('download_app_to_track_warranty')}
                        </p>
                        <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Warranty Active
                            </div>
                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Full Support
                            </div>
                        </div>
                    </div>

                    {/* Enhanced App Download Buttons */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-bold text-gray-800 mb-4">Download Our App</h4>
                        <div className="flex flex-col sm:flex-row justify-center gap-4">
                            <a 
                                href="https://play.google.com/store/apps/details?id=com.prodyt.app" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="group flex items-center bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-2xl hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-green-300/50"
                            >
                                <svg className="w-8 h-8 mr-3" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                                </svg>
                                <div className="text-left">
                                    <div className="text-xs opacity-90">Get it on</div>
                                    <div className="text-sm font-bold">Google Play</div>
                                </div>
                                <svg className="w-5 h-5 ml-3 transform group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </a>
                            <a 
                                href="https://apps.apple.com/in/app/prodyt/id6742870082" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="group flex items-center bg-gradient-to-r from-gray-800 to-black text-white px-6 py-3 rounded-2xl hover:from-gray-900 hover:to-gray-800 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-gray-400/30"
                            >
                                <svg className="w-8 h-8 mr-3" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                                </svg>
                                <div className="text-left">
                                    <div className="text-xs opacity-90">Download on the</div>
                                    <div className="text-sm font-bold">App Store</div>
                                </div>
                                <svg className="w-5 h-5 ml-3 transform group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </a>
                        </div>
                        
                        <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                            <div className="flex items-center justify-center">
                                <svg className="w-5 h-5 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                <p className="text-amber-700 text-sm font-medium">Your warranty is already active and trackable</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AlreadyRegistered;
